import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Lock, BookOpen, Shield, Clock, X } from 'lucide-react';
// --- Animated Background Waves ---
const AnimatedWaves = ({
  className = ''
}: {
  className?: string;
}) => {
  return <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-id="element-2">
      {/* Wave 1 - Slow, large amplitude */}
      <motion.svg className="absolute w-[200%] h-32 md:h-48 top-1/4 -left-1/4" viewBox="0 0 1440 120" preserveAspectRatio="none" initial={{
      x: 0
    }} animate={{
      x: [0, -720, 0]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-3">
        <motion.path d="M0,60 C180,120 360,0 540,60 C720,120 900,0 1080,60 C1260,120 1440,0 1620,60 C1800,120 1980,0 2160,60" fill="none" stroke="rgba(201, 169, 98, 0.08)" strokeWidth="2" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 1
      }} transition={{
        duration: 2,
        ease: 'easeOut'
      }} data-id="element-4" />
      </motion.svg>

      {/* Wave 2 - Medium speed, offset */}
      <motion.svg className="absolute w-[200%] h-24 md:h-36 top-1/2 -left-1/4" viewBox="0 0 1440 100" preserveAspectRatio="none" initial={{
      x: -200
    }} animate={{
      x: [-200, -920, -200]
    }} transition={{
      duration: 25,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-5">
        <motion.path d="M0,50 C120,90 240,10 360,50 C480,90 600,10 720,50 C840,90 960,10 1080,50 C1200,90 1320,10 1440,50 C1560,90 1680,10 1800,50 C1920,90 2040,10 2160,50" fill="none" stroke="rgba(201, 169, 98, 0.05)" strokeWidth="1.5" data-id="element-6" />
      </motion.svg>

      {/* Wave 3 - Faster, smaller amplitude (like a heartbeat/pulse) */}
      <motion.svg className="absolute w-[200%] h-20 md:h-28 top-3/4 -left-1/4" viewBox="0 0 1440 80" preserveAspectRatio="none" initial={{
      x: -100
    }} animate={{
      x: [-100, -820, -100]
    }} transition={{
      duration: 15,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-7">
        <motion.path d="M0,40 C90,60 180,20 270,40 C360,60 450,20 540,40 C630,60 720,20 810,40 C900,60 990,20 1080,40 C1170,60 1260,20 1350,40 C1440,60 1530,20 1620,40 C1710,60 1800,20 1890,40 C1980,60 2070,20 2160,40" fill="none" stroke="rgba(201, 169, 98, 0.06)" strokeWidth="1" data-id="element-8" />
      </motion.svg>
    </div>;
};
// --- Pulse Glow Effect ---
const PulseGlow = ({
  className = ''
}: {
  className?: string;
}) => {
  return <motion.div className={`absolute rounded-full bg-[#c9a962]/10 blur-3xl ${className}`} animate={{
    scale: [1, 1.2, 1],
    opacity: [0.1, 0.2, 0.1]
  }} transition={{
    duration: 8,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-9" />;
};
// --- Components ---
const Section = ({
  children,
  className = '',
  darker = false,
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  darker?: boolean;
  id?: string;
}) => <section id={id} className={`py-10 md:py-20 px-4 md:px-6 ${darker ? 'bg-[#0a0d12]' : ''} ${className}`} data-id="element-10">
    <div className="max-w-3xl mx-auto" data-id="element-11">{children}</div>
  </section>;
const Button = ({
  children,
  secondary = false,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  secondary?: boolean;
  className?: string;
  onClick?: () => void;
}) => <button onClick={onClick} className={`
      w-full md:w-auto px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold transition-all duration-200 transform hover:-translate-y-0.5
      ${secondary ? 'bg-transparent border-2 border-[#c9a962] text-[#c9a962] hover:bg-[#c9a962]/10' : 'bg-[#c9a962] text-[#0f1419] hover:bg-[#d4b46e] shadow-lg shadow-[#c9a962]/20'}
      ${className}
    `} data-id="element-12">
    {children}
  </button>;
const CheckItem = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="flex items-start space-x-3 mb-3" data-id="element-13">
    <div className="mt-0.5 flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#c9a962]/20 flex items-center justify-center" data-id="element-14">
      <Check className="w-3 h-3 md:w-4 md:h-4 text-[#c9a962]" data-id="element-15" />
    </div>
    <p className="text-base md:text-lg text-[#f5f0e8]/90 leading-relaxed" data-id="element-16">
      {children}
    </p>
  </div>;
const Card = ({
  title,
  children,
  icon: Icon
}: {
  title: string;
  children: React.ReactNode;
  icon?: any;
}) => <div className="bg-[#1a1f2e] border border-[#ffffff]/5 p-5 md:p-8 rounded-xl shadow-xl mb-4 md:mb-6" data-id="element-17">
    {Icon && <Icon className="w-6 h-6 md:w-8 md:h-8 text-[#c9a962] mb-3 md:mb-4" data-id="element-18" />}
    <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#f5f0e8]" data-id="element-19">
      {title}
    </h3>
    <p className="text-sm md:text-base text-[#f5f0e8]/80 leading-relaxed" data-id="element-20">
      {children}
    </p>
  </div>;
const PrivacyModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-21">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-22" />
      <div className="relative bg-[#1a1f2e] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-[#ffffff]/10 shadow-2xl" data-id="element-23">
        <div className="sticky top-0 bg-[#1a1f2e] p-4 md:p-6 border-b border-[#ffffff]/10 flex justify-between items-center" data-id="element-24">
          <h2 className="text-xl md:text-2xl font-bold text-[#f5f0e8]" data-id="element-25">
            Privacy Policy
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#ffffff]/10 rounded-lg transition-colors" data-id="element-26">
            <X className="w-5 h-5 text-[#f5f0e8]/70" data-id="element-27" />
          </button>
        </div>
        <div className="p-4 md:p-6 text-[#f5f0e8]/80 text-sm md:text-base space-y-4" data-id="element-28">
          <p className="text-[#f5f0e8]/60 text-xs md:text-sm" data-id="element-29">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-30">
            1. Information We Collect
          </h3>
          <p data-id="element-31">
            When you purchase The Spectatoring Reset (50+), we collect only the
            information necessary to process your order:
          </p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-32">
            <li data-id="element-33">Email address (for delivery of your PDF)</li>
            <li data-id="element-34">
              Payment information (processed securely through our payment
              provider)
            </li>
          </ul>
          <p data-id="element-35">
            We do not collect or store sensitive health information. Your
            purchase is completely private.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-36">
            2. How We Use Your Information
          </h3>
          <p data-id="element-37">Your information is used solely to:</p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-38">
            <li data-id="element-39">Deliver your purchased digital product</li>
            <li data-id="element-40">Send order confirmation and receipt</li>
            <li data-id="element-41">Provide customer support if needed</li>
          </ul>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-42">
            3. Your Privacy is Protected
          </h3>
          <p data-id="element-43">
            We understand the sensitive nature of this product. Your purchase
            will appear discreetly on billing statements. We never share, sell,
            or disclose your information to third parties for marketing
            purposes.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-44">
            4. Data Security
          </h3>
          <p data-id="element-45">
            All transactions are encrypted using industry-standard SSL
            technology. Payment processing is handled by trusted third-party
            providers who comply with PCI-DSS standards.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-46">
            5. Your Rights
          </h3>
          <p data-id="element-47">
            You may request deletion of your data at any time by contacting us.
            We will remove your information from our systems within 30 days of
            your request.
          </p>

          <h3 className="text-lg font-semibold text-[#f5f0e8] mt-6" data-id="element-48">
            6. Contact
          </h3>
          <p data-id="element-49">
            For any privacy-related questions or concerns, please contact our
            support team. We take your privacy seriously and will respond
            promptly to any inquiries.
          </p>

          <div className="pt-6 border-t border-[#ffffff]/10 mt-6" data-id="element-50">
            <Button onClick={onClose} className="w-full" data-id="element-51">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>;
};
// Scroll to pricing function
const scrollToPricing = () => {
  document.getElementById('pricing')?.scrollIntoView({
    behavior: 'smooth'
  });
};
// --- Main Page ---
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  return <div className="min-h-screen bg-[#0f1419] text-[#f5f0e8] overflow-x-hidden relative" data-id="element-52">
      {/* Global Background Waves */}
      <div className="fixed inset-0 pointer-events-none z-0" data-id="element-53">
        <AnimatedWaves data-id="element-54" />
        <PulseGlow className="w-96 h-96 -top-48 -right-48" data-id="element-55" />
        <PulseGlow className="w-80 h-80 top-1/3 -left-40" data-id="element-56" />
        <PulseGlow className="w-64 h-64 bottom-1/4 right-1/4" data-id="element-57" />
      </div>

      {/* SECTION 1: HERO */}
      <Section className="pt-12 pb-8 md:pt-24 md:pb-16 relative z-10" data-id="element-58">
        <div className="text-center mb-8 md:mb-12" data-id="element-59">
          <span className="inline-block px-3 py-1 mb-4 md:mb-6 text-xs font-semibold tracking-widest text-[#c9a962] uppercase bg-[#c9a962]/10 rounded-full" data-id="element-60">
            For Men 50+
          </span>
          <h1 className="text-3xl md:text-6xl font-bold leading-tight mb-4 md:mb-6 text-[#f5f0e8]" data-id="element-61">
            You're Not "Broken." <br data-id="element-62" />
            <span className="text-[#c9a962]" data-id="element-63">You're Monitoring Yourself.</span>
          </h1>
          <p className="text-lg md:text-2xl text-[#f5f0e8]/80 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-64">
            The 90-Second Reset for the Moment You Start Panicking
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-65">
          <div className="space-y-4 md:space-y-6" data-id="element-66">
            <p className="text-base md:text-lg leading-relaxed text-[#f5f0e8]/90" data-id="element-67">
              You're not failing because you're 50+. You're failing because, at
              the exact moment intimacy starts, your brain turns into a
              surveillance camera.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#f5f0e8]/90" data-id="element-68">
              You start checking.
              <br data-id="element-69" />
              You start guessing what she's thinking.
              <br data-id="element-70" />
              You start trying to "make it happen."
            </p>
            <p className="text-base md:text-lg font-semibold text-[#f5f0e8]" data-id="element-71">
              And the checking kills it.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#f5f0e8]/90" data-id="element-72">
              <strong className="text-[#c9a962]" data-id="element-73">
                The Spectatoring Reset (50+)
              </strong>{' '}
              is a private 14-day field manual that gives you a step-by-step
              protocol for the moment the spiral starts—so you can stay present,
              lead the pace, and stop treating sex like an exam.
            </p>
            <p className="text-base md:text-lg italic text-[#f5f0e8]/70 border-l-2 border-[#c9a962] pl-4" data-id="element-74">
              No cringe. No "confidence speeches." Just a repeatable sequence.
            </p>

            <div className="pt-2 md:pt-4" data-id="element-75">
              <Button onClick={scrollToPricing} className="w-full md:w-auto" data-id="element-76">
                Get The Private Reset — $17
              </Button>
              <p className="mt-2 md:mt-3 text-xs md:text-sm text-[#f5f0e8]/50 flex items-center justify-center md:justify-start" data-id="element-77">
                <Lock className="w-3 h-3 mr-1" data-id="element-78" /> Secure checkout. Instant PDF
                download.
              </p>
            </div>
          </div>

          <div className="relative" data-id="element-79">
            <div className="absolute inset-0 bg-[#c9a962]/20 blur-3xl rounded-full opacity-20" data-id="element-80"></div>
            <img src="https://cdn.magicpatterns.com/uploads/2RcNuyMjKFdKQikvPnQBn6/heylloyd-image-1770072597630.png" alt="The Spectatoring Reset Guide on tablet" className="relative z-10 rounded-lg shadow-2xl border border-[#ffffff]/10 w-full" data-id="element-81" />
          </div>
        </div>
      </Section>

      {/* SECTION 2: THE PROBLEM */}
      <Section darker className="border-y border-[#ffffff]/5 relative z-10" data-id="element-82">
        <div className="max-w-2xl mx-auto" data-id="element-83">
          <h2 className="text-2xl md:text-4xl font-bold mb-6 md:mb-8 text-center" data-id="element-84">
            This is the part nobody tells you:
          </h2>

          <div className="space-y-4 md:space-y-6 text-base md:text-lg text-[#f5f0e8]/80 leading-relaxed" data-id="element-85">
            <p data-id="element-86">Sometimes the more you like her… the worse it gets.</p>
            <p data-id="element-87">Because now it matters.</p>
            <p data-id="element-88">
              And when it matters, your brain starts running a silent background
              check:
            </p>

            <ul className="pl-4 md:pl-6 space-y-2 border-l-2 border-[#c9a962]/30 my-4 md:my-6" data-id="element-89">
              <li className="pl-2" data-id="element-90">"Is it happening yet?"</li>
              <li className="pl-2" data-id="element-91">"Is she noticing?"</li>
              <li className="pl-2" data-id="element-92">"What if I lose it again?"</li>
              <li className="pl-2" data-id="element-93">"What does this say about me?"</li>
            </ul>

            <p data-id="element-94">
              That internal scan doesn't feel like fear. It feels like "being
              realistic."
            </p>
            <p data-id="element-95">But your body reads it as danger.</p>
            <p data-id="element-96">
              And when your nervous system flips into threat mode, arousal
              becomes the first thing that disappears.
            </p>
            <p className="text-lg md:text-xl font-medium text-[#f5f0e8] pt-2 md:pt-4" data-id="element-97">
              Not because you're broken.
              <br data-id="element-98" />
              <span className="text-[#c9a962]" data-id="element-99">
                Because you're monitoring yourself.
              </span>
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 3: IDENTIFICATION */}
      <Section className="relative z-10" data-id="element-100">
        <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center" data-id="element-101">
          <div className="order-2 md:order-1" data-id="element-102">
            <img src="https://cdn.magicpatterns.com/uploads/em594LMRL5VrGh7DZJLHCS/heylloyd-image-1770073164544.png" alt="Man contemplating" className="rounded-lg shadow-2xl border border-[#ffffff]/10 w-full" data-id="element-103" />
          </div>

          <div className="order-1 md:order-2" data-id="element-104">
            <h2 className="text-2xl md:text-4xl font-bold mb-6 md:mb-8" data-id="element-105">
              If any of this is true, you're the target
            </h2>

            <div className="space-y-1" data-id="element-106">
              <CheckItem data-id="element-107">
                You can get erections alone (or in the morning)… but with a
                partner you spiral.
              </CheckItem>
              <CheckItem data-id="element-108">
                One bad night created a fear that now shows up every time.
              </CheckItem>
              <CheckItem data-id="element-109">
                You rush because you're afraid your body will "change its mind."
              </CheckItem>
              <CheckItem data-id="element-110">You read her face like it's a verdict.</CheckItem>
              <CheckItem data-id="element-111">
                You've tried pills, tips, supplements, "relax," porn breaks… and
                you still can't control the moment.
              </CheckItem>
            </div>

            <p className="mt-6 md:mt-8 text-base md:text-lg font-medium text-[#c9a962] border-t border-[#ffffff]/10 pt-4 md:pt-6" data-id="element-112">
              This guide is for the man who doesn't want to talk about it for
              six months. He wants something he can use tonight.
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 4: WHY OLD SOLUTIONS FAIL */}
      <Section darker className="text-center border-y border-[#ffffff]/5 relative z-10" data-id="element-113">
        <div className="max-w-2xl mx-auto" data-id="element-114">
          <h2 className="text-2xl md:text-4xl font-bold mb-6 md:mb-8" data-id="element-115">
            Why "just relax" fails
          </h2>

          <p className="text-lg md:text-xl text-[#f5f0e8]/90 mb-4 md:mb-6" data-id="element-116">
            Because "relax" isn't an instruction.
          </p>

          <p className="text-base md:text-lg text-[#f5f0e8]/70 mb-6 md:mb-8" data-id="element-117">
            It's like telling someone in a skid to "drive better."
          </p>

          <div className="bg-[#1a1f2e] p-6 md:p-8 rounded-xl border border-[#c9a962]/20 shadow-lg" data-id="element-118">
            <p className="text-lg md:text-xl font-medium leading-relaxed" data-id="element-119">
              When the spiral hits, you need a{' '}
              <span className="text-[#c9a962]" data-id="element-120">sequence</span>—a set of moves
              that change your state and redirect your attention, fast.
            </p>
            <p className="mt-3 md:mt-4 text-[#f5f0e8]/60 uppercase tracking-widest text-xs md:text-sm font-bold" data-id="element-121">
              That's what this is.
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 5: THE MECHANISM */}
      <Section className="relative overflow-hidden z-10" data-id="element-122">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl bg-[#c9a962]/5 blur-[100px] -z-10" data-id="element-123"></div>

        <div className="text-center mb-8 md:mb-16" data-id="element-124">
          <h2 className="text-xs md:text-sm font-bold tracking-widest text-[#c9a962] uppercase mb-2 md:mb-3" data-id="element-125">
            Introducing
          </h2>
          <h3 className="text-2xl md:text-5xl font-bold mb-3 md:mb-4" data-id="element-126">
            The Spectatoring Reset (50+)
          </h3>
          <p className="text-base md:text-xl text-[#f5f0e8]/70" data-id="element-127">
            A 14-day, step-by-step protocol built around one mechanism:
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-8 md:mb-16" data-id="element-128">
          <div className="bg-[#c9a962]/10 border border-[#c9a962]/30 rounded-2xl p-5 md:p-12 text-center" data-id="element-129">
            <h4 className="text-xl md:text-3xl font-bold text-[#c9a962] mb-4 md:mb-6 font-serif" data-id="element-130">
              The Spectatoring Kill-Switch Protocol
            </h4>
            <img src="https://cdn.magicpatterns.com/uploads/rWJxdGbwYTn7PHLMNr7P8d/heylloyd-image-1770060089670.png" alt="Downshift → Re-Anchor → No-Fail Ladder" className="w-full max-w-xl mx-auto" data-id="element-131" />
          </div>
          <p className="text-center mt-6 md:mt-8 text-base md:text-lg text-[#f5f0e8]/70 max-w-2xl mx-auto" data-id="element-132">
            It's not motivation. It's not affirmations. It's not a "be more
            confident" lecture. It's a field manual for the moment you start
            checking yourself.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-133">
          <Card title="1) Downshift" icon={Clock} data-id="element-134">
            <span className="block text-xs md:text-sm text-[#c9a962] mb-2 font-mono" data-id="element-135">
              60–90 SECONDS
            </span>
            A discreet nervous-system reset you can do while kissing, touching,
            or stepping away for a moment.
            <br data-id="element-136" />
            <br data-id="element-137" />
            <strong className="text-[#f5f0e8]" data-id="element-138">Purpose:</strong> stop your body
            from treating intimacy like a threat.
          </Card>

          <Card title="2) Re-Anchor" icon={Shield} data-id="element-139">
            <span className="block text-xs md:text-sm text-[#c9a962] mb-2 font-mono" data-id="element-140">
              ATTENTION CONTROL
            </span>
            A simple attention strategy that pulls you out of scoreboard
            thinking and back into sensation and connection.
            <br data-id="element-141" />
            <br data-id="element-142" />
            <strong className="text-[#f5f0e8]" data-id="element-143">Purpose:</strong> break
            spectatoring in real time.
          </Card>

          <Card title="3) No-Fail Ladder" icon={BookOpen} data-id="element-144">
            <span className="block text-xs md:text-sm text-[#c9a962] mb-2 font-mono" data-id="element-145">
              PROGRESSION
            </span>
            A progression that removes the single pass/fail moment.
            <br data-id="element-146" />
            <br data-id="element-147" />
            <strong className="text-[#f5f0e8]" data-id="element-148">Purpose:</strong> even if you
            wobble, you don't panic—because you already know what to do next.
          </Card>
        </div>
      </Section>

      {/* SECTION 6: WHY OLD SOLUTIONS MAKE IT WORSE */}
      <Section darker className="border-y border-[#ffffff]/5 relative z-10" data-id="element-149">
        <h2 className="text-2xl md:text-4xl font-bold mb-8 md:mb-12 text-center" data-id="element-150">
          Why old solutions keep making it worse
        </h2>

        <div className="space-y-4 md:space-y-6 max-w-2xl mx-auto" data-id="element-151">
          <div className="flex gap-3 md:gap-4" data-id="element-152">
            <div className="w-1 bg-red-500/20 rounded-full flex-shrink-0" data-id="element-153"></div>
            <div data-id="element-154">
              <h4 className="text-lg md:text-xl font-bold text-[#f5f0e8] mb-1 md:mb-2" data-id="element-155">
                Pills alone
              </h4>
              <p className="text-sm md:text-base text-[#f5f0e8]/70" data-id="element-156">
                Can work physically but increase psychological pressure: "Now I{' '}
                <em data-id="element-157">have</em> to perform."
              </p>
            </div>
          </div>

          <div className="flex gap-3 md:gap-4" data-id="element-158">
            <div className="w-1 bg-red-500/20 rounded-full flex-shrink-0" data-id="element-159"></div>
            <div data-id="element-160">
              <h4 className="text-lg md:text-xl font-bold text-[#f5f0e8] mb-1 md:mb-2" data-id="element-161">
                Random tips
              </h4>
              <p className="text-sm md:text-base text-[#f5f0e8]/70" data-id="element-162">
                Don't solve the moment of panic.
              </p>
            </div>
          </div>

          <div className="flex gap-3 md:gap-4" data-id="element-163">
            <div className="w-1 bg-red-500/20 rounded-full flex-shrink-0" data-id="element-164"></div>
            <div data-id="element-165">
              <h4 className="text-lg md:text-xl font-bold text-[#f5f0e8] mb-1 md:mb-2" data-id="element-166">
                Trying harder
              </h4>
              <p className="text-sm md:text-base text-[#f5f0e8]/70" data-id="element-167">
                Makes you monitor harder. And monitoring is the match that
                lights the spiral.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* SECTION 7: WHAT YOU GET */}
      <Section className="relative z-10" data-id="element-168">
        <div className="bg-[#151a23] border border-[#ffffff]/5 rounded-2xl p-5 md:p-12 shadow-2xl" data-id="element-169">
          <h2 className="text-2xl md:text-4xl font-bold mb-6 md:mb-8 text-center" data-id="element-170">
            What you get inside
          </h2>

          <div className="grid md:grid-cols-2 gap-x-8 md:gap-x-12 gap-y-2 md:gap-y-4" data-id="element-171">
            <CheckItem data-id="element-172">
              The exact loop that causes "works alone / fails with her"
            </CheckItem>
            <CheckItem data-id="element-173">
              The 90-second Downshift sequence (do it discreetly)
            </CheckItem>
            <CheckItem data-id="element-174">3 Re-Anchors (pick one and install it)</CheckItem>
            <CheckItem data-id="element-175">
              The No-Fail Ladder so you stop "testing yourself"
            </CheckItem>
            <CheckItem data-id="element-176">
              A short pre-game script that removes pressure without dumping a
              confession
            </CheckItem>
            <CheckItem data-id="element-177">
              A 14-day plan with trackers and a night-of checklist
            </CheckItem>
            <CheckItem data-id="element-178">
              Relapse-proofing: what to do after a bad night so it doesn't
              become your identity
            </CheckItem>
          </div>
        </div>
      </Section>

      {/* SECTION 8: WHO THIS IS FOR */}
      <Section darker className="text-center relative z-10" data-id="element-179">
        <h2 className="text-2xl md:text-4xl font-bold mb-8 md:mb-12" data-id="element-180">
          Who this is for (and not for)
        </h2>

        <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-181">
          <div className="bg-[#1a1f2e] p-5 md:p-8 rounded-xl border-t-4 border-[#c9a962]" data-id="element-182">
            <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-[#c9a962]" data-id="element-183">
              For
            </h3>
            <p className="text-sm md:text-base text-[#f5f0e8]/80" data-id="element-184">
              Men 50+ who are dating again and feel that "judged / replaced /
              humiliated" pressure.
            </p>
          </div>

          <div className="bg-[#1a1f2e] p-5 md:p-8 rounded-xl border-t-4 border-gray-600" data-id="element-185">
            <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-gray-400" data-id="element-186">
              Not For
            </h3>
            <p className="text-sm md:text-base text-[#f5f0e8]/80" data-id="element-187">
              Anyone looking for medical diagnosis, or expecting a guaranteed
              outcome from a PDF.
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 9: FINAL CTA */}
      <Section id="pricing" className="text-center pb-12 md:pb-24 relative z-10" data-id="element-188">
        <div className="max-w-2xl mx-auto" data-id="element-189">
          <h2 className="text-lg md:text-2xl font-bold mb-4 md:mb-6 text-[#f5f0e8]/60 uppercase tracking-widest" data-id="element-190">
            The point is simple:
          </h2>
          <p className="text-2xl md:text-4xl font-serif font-bold mb-8 md:mb-12 leading-tight" data-id="element-191">
            You don't need to be fearless. <br data-id="element-192" />
            <span className="text-[#c9a962]" data-id="element-193">
              You need to be skilled at the moment fear shows up.
            </span>
          </p>

          <div className="bg-[#1a1f2e] p-6 md:p-8 rounded-2xl border border-[#c9a962]/30 shadow-2xl w-full max-w-md mx-auto" data-id="element-194">
            <div className="mb-4 md:mb-6" data-id="element-195">
              <span className="text-4xl md:text-5xl font-bold text-[#f5f0e8]" data-id="element-196">
                $17
              </span>
              <span className="text-[#f5f0e8]/50 ml-2 text-base md:text-lg" data-id="element-197">
                one-time
              </span>
            </div>

            <Button className="w-full text-base md:text-lg py-4 md:py-5 mb-3 md:mb-4" data-id="element-198">
              Download The 14-Day Protocol
            </Button>

            <p className="text-xs md:text-sm text-[#f5f0e8]/50 flex items-center justify-center" data-id="element-199">
              <Lock className="w-3 h-3 mr-1" data-id="element-200" /> Instant PDF download. Private.
              No subscription.
            </p>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="py-6 md:py-8 px-4 text-center border-t border-[#ffffff]/5 relative z-10" data-id="element-201">
        <div className="max-w-3xl mx-auto" data-id="element-202">
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 mb-4" data-id="element-203">
            <button onClick={() => setPrivacyOpen(true)} className="text-[#f5f0e8]/50 hover:text-[#c9a962] text-sm transition-colors" data-id="element-204">
              Privacy Policy
            </button>
            <span className="hidden md:inline text-[#f5f0e8]/20" data-id="element-205">|</span>
            <button onClick={scrollToPricing} className="text-[#f5f0e8]/50 hover:text-[#c9a962] text-sm transition-colors" data-id="element-206">
              Get The Guide
            </button>
          </div>
          <p className="text-[#f5f0e8]/30 text-xs md:text-sm" data-id="element-207">
            © {new Date().getFullYear()} The Spectatoring Reset. All rights
            reserved.
          </p>
          <p className="text-[#f5f0e8]/20 text-xs mt-2" data-id="element-208">
            This product is for educational purposes only and is not intended to
            diagnose, treat, or cure any medical condition.
          </p>
        </div>
      </footer>

      {/* Privacy Modal */}
      <PrivacyModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} data-id="element-209" />
    </div>;
}